<script>
  /**
   * @event {null} open
   * @event {null} close
   */

  /**
   * set the text to display in the tooltip
   * @type {string}
   */
  export let text = "";

  /**
   * Set the alignment of the tooltip relative to the icon
   * @type {"start" | "center" | "end"}
   */
  export let align = "center";

  /**
   * Set the direction of the tooltip relative to the button
   * @type {"top" | "right" | "bottom" | "left"}
   */
  export let direction = "bottom";

  /**
   * Set to `true` to open the tooltip
   * @type {boolean}
   */
  export let open = false;

  /**
   * Set to `true` to hide the tooltip icon
   * @type {boolean}
   */
  export let hideIcon = false;

  /**
   * Specify the icon to render for the tooltip button.
   * Default to `<Information />`
   * @type {typeof import("svelte").SvelteComponent<any>}
   */
  export let icon = Information;

  /** Specify the ARIA label for the tooltip button */
  export let iconDescription = "";

  /** Specify the icon name attribute */
  export let iconName = "";

  /** Set the button tabindex */
  export let tabindex = "0";

  /**
   * Set an id for the tooltip
   * @type {string}
   */
  export let tooltipId = "ccs-" + Math.random().toString(36);

  /**
   * Set an id for the tooltip button
   * @type {string}
   */
  export let triggerId = "ccs-" + Math.random().toString(36);

  /** Set the tooltip button text */
  export let triggerText = "";

  /** Obtain a reference to the trigger text HTML element */
  export let ref = null;

  /** Use an existing element as the trigger without rendering another control. */
  export let triggerRef = null;

  /** Obtain a reference to the tooltip HTML element */
  export let refTooltip = null;

  /** Obtain a reference to the icon HTML element */
  export let refIcon = null;

  /**
   * The tooltip opens only when the trigger button is clicked.
   * @type {boolean}
   */
  export let openOnClick = false;

  import { createEventDispatcher, afterUpdate, setContext } from "svelte";
  import { writable } from "svelte/store";
  import Information from "../icons/Info.svelte";

  $: if (triggerRef) ref = triggerRef;

  const dispatch = createEventDispatcher();
  const tooltipOpen = writable(open);

  setContext("Tooltip", { tooltipOpen });

  function onKeydown(e) {
    if (e.key === "Escape" || e.key === "Tab") {
      e.stopPropagation();
      if (e.key === "Escape") refIcon?.focus();
      open = false;
    } else if (e.key === " " || e.key === "Enter") {
      e.stopPropagation();
      e.preventDefault();
      open = true;
    }
  }

  function onBlur({ relatedTarget }) {
    if (refTooltip && !refTooltip.contains(relatedTarget)) {
      open = false;
    }
  }

  function onFocus() {
    open = true;
  }

  function onMousedown() {
    // determine the desired state before the focus event triggers.
    if (!openOnClick) return;
    const shouldClose = open;
    // ensure changes are scheduled at the end, i.e. after the possible focus event.
    setTimeout(() => {
      open = shouldClose ? false : true;
    });
  }

  // Moves the tooltip node to document.body so it is not clipped by any
  // ancestor stacking context (overflow:hidden, z-index, transform…).
  function portal(node) {
    document.body.appendChild(node);
    return {
      destroy() {
        node.parentNode && node.parentNode.removeChild(node);
      },
    };
  }

  afterUpdate(() => {
    if (open) {
      const button = ref.getBoundingClientRect();
      const tooltip = refTooltip.getBoundingClientRect();

      let iconWidth = 16;
      let iconHeight = 16;

      if (refIcon) {
        const icon = refIcon.getBoundingClientRect();
        iconWidth = icon.width;
        iconHeight = icon.height;
      }

      // When the tooltip is portalled to <body> we need absolute page coords.
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;

      let x = 0;
      let y = 0;

      switch (direction) {
        case "bottom":
          if (hideIcon) {
            x = button.left + scrollX - (tooltip.width / 2 - button.width / 2);
          } else {
            x =
              button.left +
              scrollX -
              (tooltip.width / 2 - button.width + iconWidth / 2);
          }
          y = button.bottom + scrollY + iconHeight / 2;
          break;
        case "right":
          x = button.right + scrollX + 6;
          y = button.top + scrollY - (iconWidth / 2 + 4);
          break;
        case "left":
          if (hideIcon) {
            x = button.left + scrollX - (tooltip.width + 6 + 1);
          } else {
            x =
              button.left +
              scrollX -
              (tooltip.width - button.width + iconWidth + 8);
          }
          y = button.top + scrollY - (iconWidth / 2 + 4);
          break;
        case "top":
          if (hideIcon) {
            x = button.left + scrollX - (tooltip.width / 2 - button.width / 2);
          } else {
            x =
              button.left +
              scrollX -
              (tooltip.width / 2 - button.width + iconWidth / 2 + 1);
          }
          y =
            button.top +
            scrollY -
            (tooltip.height + button.height + iconWidth / 2 - 1);
          break;
      }

      // Clamp horizontal position to keep tooltip inside viewport.
      const viewportPadding = 8;
      const minX = scrollX + viewportPadding;
      const maxX =
        scrollX + window.innerWidth - tooltip.width - viewportPadding;
      x = Math.max(minX, Math.min(x, Math.max(minX, maxX)));

      refTooltip.style.position = "absolute";
      refTooltip.style.left = x + "px";
      refTooltip.style.top = y + "px";
      refTooltip.style.marginTop = "0";
    }
  });
  $: textLength = text.length;
  $: tooltipOpen.set(open);
  $: dispatch(open ? "open" : "close");
  $: buttonProps = {
    role: "button",
    "aria-haspopup": "true",
    id: hideIcon ? triggerId : undefined,
    class: hideIcon ? "bx--tooltip__label" : "bx--tooltip__trigger",
    "aria-expanded": open,
    "aria-describedby": open ? tooltipId : undefined,
    "aria-labelledby": triggerText ? triggerId : undefined,
    "aria-label": triggerText ? undefined : iconDescription,
    tabindex,
    style: hideIcon ? $$restProps.style : undefined,
  };
</script>

<svelte:window
  on:mousedown="{({ target }) => {
    if (open) {
      if (target.contains(refTooltip)) {
        if (refIcon) {
          refIcon.focus();
        } else if (ref) {
          ref.focus();
        }
      }
    }
  }}"
  on:click|capture="{({ target }) => {
    if (open && !ref.contains(target) && !refTooltip.contains(target)) {
      setTimeout(() => {
        open = false;
      });
    }
  }}"
/>

<div
  style:position="relative"
  style:display="{triggerRef ? 'contents' : undefined}"
  class:bx--tooltip-wrapper="{true}"
  {...$$restProps}
>
  {#if !hideIcon}
    <div bind:this="{ref}" id="{triggerId}" class:bx--tooltip__label="{true}">
      <slot name="triggerText">{triggerText}</slot>
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        bind:this="{refIcon}"
        {...buttonProps}
        aria-describedby="{tooltipId}"
        on:mouseenter="{() => {
          if (!openOnClick) open = true;
        }}"
        on:mouseleave="{() => {
          if (!openOnClick) open = false;
        }}"
        on:mousedown="{onMousedown}"
        on:focus="{onFocus}"
        on:keydown="{onKeydown}"
      >
        <slot name="icon">
          <svelte:component
            this="{icon}"
            name="{iconName}"
            width="14"
            height="14"
          />
        </slot>
      </div>
    </div>
  {:else}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      bind:this="{ref}"
      {...buttonProps}
      aria-describedby="{tooltipId}"
      on:mousedown="{onMousedown}"
      on:focus="{onFocus}"
      on:blur="{onBlur}"
      on:keydown="{onKeydown}"
      on:mouseenter="{() => {
        if (!openOnClick) open = true;
      }}"
      on:mouseleave="{() => {
        if (!openOnClick) open = false;
      }}"
    >
      <slot name="triggerText">{triggerText}</slot>
    </div>
  {/if}
  {#if open}
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      use:portal
      bind:this="{refTooltip}"
      id="{tooltipId}"
      data-floating-menu-direction="{direction}"
      style:z-index="9000"
      style:max-width="calc(100vw - 16px)"
      style:box-sizing="border-box"
      style:width="{textLength && textLength < 55
        ? textLength + 5 + "ch"
        : "60ch"}"
      class:bx--tooltip="{true}"
      class:bx--tooltip--shown="{open}"
      class:bx--tooltip--top="{direction === 'top'}"
      class:bx--tooltip--right="{direction === 'right'}"
      class:bx--tooltip--bottom="{direction === 'bottom'}"
      class:bx--tooltip--left="{direction === 'left'}"
      class:bx--tooltip--align-center="{align === 'center'}"
      class:bx--tooltip--align-start="{align === 'start'}"
      class:bx--tooltip--align-end="{align === 'end'}"
      class="tooltip-box"
      on:keydown="{onKeydown}"
    >
      <span class:bx--tooltip__caret="{true}"></span>
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
      <div
        on:click|stopPropagation
        on:mousedown|stopPropagation
        class:bx--tooltip__content="{true}"
        tabindex="-1"
        role="dialog"
      >
        <slot>{text}</slot>
      </div>
    </div>
  {/if}
</div>

<style>
  .tooltip-box {
    background-color: var(--pax-black);
    border-radius: 0.5rem;
    color: white;
  }

  .bx--tooltip__trigger:focus {
    outline: none;
  }

  .bx--tooltip-wrapper {
    position: relative;
    display: inline-block;
  }

  .bx--tooltip__label {
    gap: 0.3rem;
  }
</style>
